import { ApolloError } from 'apollo-client';

/**
 * Stable, non-localized failure reason codes for the partnership-linking flows.
 *
 * Passed as router state to `/partner-linking/error` and logged to Datadog so
 * the failure branches can be told apart. NEVER rendered — guests always see the
 * single generic "Something went wrong" screen.
 */
export enum PartnerLinkingErrorReason {
  InitiateMalformedToken = 'INITIATE_MALFORMED_TOKEN',
  // Distinct from malformed because it points at a slow flow or a stale deep
  // link rather than a broken handoff. Only reaches the error screen when the
  // browser has a session of its own; otherwise the auth gate asks for sign-in.
  InitiateExpiredToken = 'INITIATE_EXPIRED_TOKEN',
  // The CT-linking flag is off for this identified user — the default for anyone
  // not explicitly targeted.
  LinkingDisabled = 'LINKING_DISABLED',
  InitiateFailed = 'INITIATE_FAILED',
  PartnerReported = 'PARTNER_REPORTED',
  MissingCodeOrState = 'MISSING_CODE_OR_STATE',
  SessionExpired = 'SESSION_EXPIRED',
  CallbackMalformedToken = 'CALLBACK_MALFORMED_TOKEN',
  CallbackExpiredToken = 'CALLBACK_EXPIRED_TOKEN',
  CompleteUnsuccessful = 'COMPLETE_UNSUCCESSFUL',
  CompleteError = 'COMPLETE_ERROR',
}

/**
 * Apollo `extensions.code` values thrown by the TH gateway during CT linking
 * (see ctg-th-loyalty-service partnership.service).
 */
export enum PartnerLinkingBackendErrorCode {
  AlreadyLinked = 'ALREADY_LINKED',
  RateLimited = 'RATE_LIMITED',
  // THLE-4044 — loyalty account outside Canada; renders region-unavailable.
  RegionNotEligible = 'REGION_NOT_ELIGIBLE',
  // THLE-4186 — signed in but email-unverified; renders verification-required.
  EmailVerificationRequired = 'EMAIL_VERIFICATION_REQUIRED',
}

/**
 * THLE-4387 — CT-side `errCode`s that get the dedicated Triangle account error
 * screen at `completeLinking` instead of the generic "Something went wrong"
 * page.
 *
 * Matched on the raw CT code (`extensions.ctErrCode`, set by the loyalty
 * service's `mapCtErrorToApollo`) rather than `extensions.code`, because they
 * collapse into three different Apollo codes — `CT_LINK_FAILED`,
 * `CT_UNAVAILABLE`, and `ALREADY_LINKED_ELSEWHERE` — so the Apollo code alone
 * cannot tell them apart from the failures that keep the generic screen.
 */
const CT_ACCOUNT_ERROR_CODES: readonly string[] = [
  '00221', // link/delink limit exceeded in the loyalty backend
  '00207', // inactive account on the CT side
  '00202', // error retrieving profile information from CIAM
  '00185', // partner card already linked to another Triangle account
  '00183', // general loyalty backend error
  // Undocumented by CT and absent from the THLE-4387 list, but it behaves the
  // same: a 400 from /op/linkPartnerCard that no retry clears. Every affected
  // guest on 2 Sep 2026 retried (one across 4.5h, one across both app and web)
  // and none ever linked. Pending a definition from CT.
  '00235',
];

/**
 * THLE-4387 — only thrown at `completeLinking`: every code above comes from
 * CT's `/op/token` or `/op/linkPartnerCard`, neither of which the initiate leg
 * calls.
 */
export function isCtAccountError(err: unknown): boolean {
  if (!(err instanceof ApolloError)) {
    return false;
  }
  return (
    err.graphQLErrors?.some(e => {
      const ctErrCode = e.extensions?.ctErrCode;
      return typeof ctErrCode === 'string' && CT_ACCOUNT_ERROR_CODES.includes(ctErrCode);
    }) ?? false
  );
}

/**
 * THLE-4044/THLE-4256 — the CT partnership is Canada-only. The backend refuses a
 * non-Canadian account at `initiateLinking`, before any OIDC redirect, and again
 * at `completeLinking` in case eligibility changes mid-flow. Shared because both
 * pages render the same screen from it.
 */
export function isRegionNotEligibleError(err: unknown): boolean {
  if (!(err instanceof ApolloError)) {
    return false;
  }
  return (
    err.graphQLErrors?.some(
      e => e.extensions?.code === PartnerLinkingBackendErrorCode.RegionNotEligible
    ) ?? false
  );
}

/**
 * THLE-4186 — thrown at `initiateLinking`, before any OIDC redirect or INITIATED
 * record, and again at `completeLinking`. Shared because both pages render the
 * same screen from it.
 */
export function isEmailVerificationRequiredError(err: unknown): boolean {
  if (!(err instanceof ApolloError)) {
    return false;
  }
  return (
    err.graphQLErrors?.some(
      e => e.extensions?.code === PartnerLinkingBackendErrorCode.EmailVerificationRequired
    ) ?? false
  );
}
